import { MarketplaceOfferJsonldMarketplaceOfferRead } from '@/api/generated';
import { yupResolver } from '@hookform/resolvers/yup';
import { useModal } from 'context/Modal/Context';
import { MODAL_NAME } from 'context/Modal/MODAL_NAME';
import { MAX_PRICE, MIN_PRICE } from 'context/Modal/views/MarketplaceActions/ManageOffer/consts';
import { useToaster } from 'context/Toaster/ToasterContext';
import { getErrorCode, getErrorMessage } from 'hooks/networkError/utils';
import useCardDetailsApi from 'hooks/queries/cards/useCardsDetailsApi';
import { useAddOffers, useCardOfferDetailApi, useEditOffers, useRemoveOffers } from 'hooks/queries/marketplace/offers';
import useUserApi from 'hooks/UserApi/useUserApi';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { VARIANT_COLOR_ENUM } from 'types/utils';
import { useCmsData } from 'utils/cms';
import * as yup from 'yup';

type FormValues = { quantity: number; price: number };

const API_ERROR = ['too-much-offers-nb', 'too-much-offers-amount'];

const useManageOffer = ({ closeModal, reference }: { closeModal: () => void; reference: string }) => {
  const { card, isLoading: isLoadingCardDetail } = useCardDetailsApi(reference);
  const { user } = useUserApi();
  const offerId = card.stats?.inMySale ? user?.id : undefined;
  const {
    queryResult: { data, isSuccess },
  } = useCardOfferDetailApi({ cardRef: reference, offerId });

  const {
    generalSettings: { salesFeesSimulation },
  } = useCmsData();

  // @ts-expect-error bugued api typed
  const cardOffer: MarketplaceOfferJsonldMarketplaceOfferRead = data?.data;

  const { addToaster } = useToaster();
  const { replaceModal } = useModal();

  const schema = () =>
    yup
      .object({
        quantity: yup
          .number()
          .min(1)
          .max(card?.stats?.inMyCollection || 0)
          .required(),
        price: yup
          .number()
          .required()
          .min(MIN_PRICE)
          .max(MAX_PRICE)
          .test('two-decimals', 'two-decimals-error', (value) => /^\d+(\.\d{0,2})?$/.test(value.toString())),
      })
      .required();

  const formMethods = useForm<FormValues>({
    mode: 'onSubmit',
    defaultValues: {
      quantity: cardOffer?.quantity || 1,
      price: cardOffer?.convertedPrice || 0,
    },
    resolver: yupResolver(schema()),
  });
  const { handleSubmit, watch, setError } = formMethods;

  const onSuccess = async (message: string) => {
    addToaster({ message, variant: VARIANT_COLOR_ENUM.SUCCESS });
    closeModal();
  };

  const onError = async (error: Error) => {
    if (getErrorCode(error) === 400) {
      if (API_ERROR.includes(error.message)) {
        replaceModal(MODAL_NAME.CMS_MODAL, { cmsModalName: 'sell-cards-reach-antifraud-limits' });
      } else {
        setError('price', {
          type: 'server',
          message: await getErrorMessage(error),
        });
      }
    } else {
      addToaster({ variant: VARIANT_COLOR_ENUM.ERROR, message: await getErrorMessage(error) });
      closeModal();
    }
  };

  const { addOffer } = useAddOffers(onSuccess, onError);
  const { updateOffer } = useEditOffers(onSuccess, onError);
  const { removeOffer } = useRemoveOffers(onSuccess, onError);
  const onSubmit = (formValues: FormValues) => {
    if (cardOffer && card['@id']) {
      updateOffer.mutateAsync({
        price: formValues.price,
        quantity: formValues.quantity,
        cardRef: card['@id'].split('/').pop() || '',
      });
    } else {
      addOffer.mutateAsync({ cards: [{ ...formValues, reference: card.reference }] });
    }
  };

  const handleOpenModalOfferDeletion = async () => {
    const result = await new Promise((resolve) => {
      replaceModal(MODAL_NAME.CONFIRM_OFFER_DELETION, {
        onSubmit: () => resolve(true),
        cardName: card?.name || '',
      });
    });
    if (result && card.reference) {
      removeOffer.mutateAsync({ cardRef: card.reference });
    }
  };

  const totalWithoutFees = watch('quantity') * watch('price');
  const total = totalWithoutFees - Math.max(totalWithoutFees * Number(salesFeesSimulation || 0), 0.03);

  useEffect(() => {
    if (cardOffer) {
      formMethods.reset({
        quantity: cardOffer?.quantity || 1,
        price: cardOffer?.convertedPrice || 0,
      });
    }
  }, [cardOffer]);

  return {
    isLoading: addOffer.isPending || updateOffer.isPending || removeOffer.isPending,
    formMethods,
    onSubmit: handleSubmit(onSubmit),
    closeModal,
    handleOpenModalOfferDeletion,
    total,
    numberCopyAvailable: card?.stats?.inMyCollection || 0,
    card,
    cardOffer,
    isLoadingCard: (!isSuccess && offerId) || isLoadingCardDetail,
    displayNetPrice: watch('price') >= MIN_PRICE,
  };
};

export default useManageOffer;
